import { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'

import { ScoreMeter } from '@/components/charts/Contributions'
import {
  AssertionBadge,
  Avatar,
  Badge,
  Card,
  EmptyState,
  ErrorNote,
  PageHeader,
  Select,
  Spinner,
  StatusBadge,
} from '@/components/ui'
import { useQuery } from '@/hooks/useQuery'
import { ApiError, api } from '@/lib/api'
import { matchTypeLabel, relativeTime, sentence } from '@/lib/format'
import { useAuth } from '@/lib/auth'
import type { Ack, Match, MatchParty, Opportunity, Page } from '@/types'

const VIEW_OPTIONS = [
  { value: 'matches', label: 'Suggested matches' },
  { value: 'atoms', label: 'Open needs & offers' },
]

const KIND_OPTIONS = [
  { value: '', label: 'Needs & offers' },
  { value: 'need', label: 'Needs only' },
  { value: 'offer', label: 'Offers only' },
]

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: '', label: 'Any status' },
  { value: 'reviewing', label: 'Reviewing' },
  { value: 'approved', label: 'Approved' },
  { value: 'introduction_requested', label: 'Awaiting consent' },
  { value: 'introduction_sent', label: 'Introduced' },
  { value: 'connected', label: 'Connected' },
  { value: 'rejected', label: 'Rejected' },
]

const TYPE_OPTIONS = [
  { value: '', label: 'Any type' },
  { value: 'customer_provider', label: 'Customer / provider' },
  { value: 'buyer_seller', label: 'Buyer / seller' },
  { value: 'referral_business', label: 'Referral' },
  { value: 'partner_partner', label: 'Partnership' },
  { value: 'employer_candidate', label: 'Employer / candidate' },
  { value: 'investor_founder', label: 'Investor / founder' },
  { value: 'mentor_learner', label: 'Mentor / learner' },
  { value: 'supplier_business', label: 'Supplier' },
  { value: 'connector_opportunity', label: 'Connector' },
]

const SCORE_OPTIONS = [
  { value: '0', label: 'Any score' },
  { value: '70', label: 'Score 70+' },
  { value: '85', label: 'Score 85+' },
]

export function OpportunitiesPage() {
  const { canApprove } = useAuth()
  const [view, setView] = useState('matches')
  const [kind, setKind] = useState('')
  const [status, setStatus] = useState('new')
  const [type, setType] = useState('')
  const [minScore, setMinScore] = useState('0')
  const [busy, setBusy] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const query = useQuery(
    useCallback(
      () =>
        api.get<Page<Match>>('/matches', {
          status: status || undefined,
          match_type: type || undefined,
          min_score: Number(minScore),
          limit: 60,
        }),
      [status, type, minScore],
    ),
    [status, type, minScore],
  )

  async function review(matchId: string, action: 'approve' | 'reject') {
    setBusy(matchId)
    setNotice(null)
    try {
      await api.post(`/matches/${matchId}/review`, { action })
      setNotice(action === 'approve' ? 'Match approved.' : 'Match rejected.')
      query.reload()
    } catch (err) {
      setNotice(err instanceof ApiError ? err.message : 'That action failed.')
    } finally {
      setBusy(null)
    }
  }

  async function recompute() {
    setBusy('recompute')
    try {
      const ack = await api.post<Ack>('/matches/recompute', { use_ai: true })
      setNotice(ack.detail ?? 'Rescan queued.')
    } catch (err) {
      setNotice(err instanceof ApiError ? err.message : 'Could not queue a rescan.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <PageHeader
        title="Opportunities"
        subtitle="Suggested connections awaiting a human decision. Nobody is contacted until you approve."
        action={
          canApprove && (
            <button
              type="button"
              className="btn-secondary"
              onClick={recompute}
              disabled={busy !== null}
            >
              {busy === 'recompute' ? 'Queueing…' : 'Rescan network'}
            </button>
          )
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Select label="View" value={view} onChange={setView} options={VIEW_OPTIONS} />
        {view === 'matches' ? (
          <>
            <Select label="Status" value={status} onChange={setStatus} options={STATUS_OPTIONS} />
            <Select label="Match type" value={type} onChange={setType} options={TYPE_OPTIONS} />
            <Select label="Minimum score" value={minScore} onChange={setMinScore} options={SCORE_OPTIONS} />
            {query.data && (
              <span className="ml-auto text-xs text-ink-muted">
                <span className="tabular">{query.data.total}</span> matching
              </span>
            )}
          </>
        ) : (
          <Select label="Kind" value={kind} onChange={setKind} options={KIND_OPTIONS} />
        )}
      </div>

      {view === 'atoms' ? (
        <OpenOpportunities kind={kind} />
      ) : (
        <>
      {notice && (
        <p className="mb-3 rounded-md px-3 py-2 text-sm text-ink" style={{ background: 'var(--surface-2)' }} role="status">
          {notice}
        </p>
      )}

      {query.loading && !query.data ? (
        <Spinner />
      ) : query.error ? (
        <Card>
          <ErrorNote message={query.error} onRetry={query.reload} />
        </Card>
      ) : !query.data?.items.length ? (
        <Card>
          <EmptyState
            title="Nothing in this queue"
            hint="Matches appear as conversations produce explicit needs and offers. Try widening the filters, or rescan the network."
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {query.data.items.map((match) => (
            <MatchCard
              key={match.id}
              match={match}
              busy={busy === match.id}
              canApprove={canApprove}
              onReview={review}
            />
          ))}
        </div>
      )}
        </>
      )}
    </>
  )
}

function OpenOpportunities({ kind }: { kind: string }) {
  const query = useQuery(
    useCallback(
      () => api.get<Page<Opportunity>>('/opportunities', { kind: kind || undefined, limit: 100 }),
      [kind],
    ),
    [kind],
  )

  if (query.loading && !query.data) return <Spinner />
  if (query.error) {
    return (
      <Card>
        <ErrorNote message={query.error} onRetry={query.reload} />
      </Card>
    )
  }
  if (!query.data?.items.length) {
    return (
      <Card>
        <EmptyState
          title="No open needs or offers"
          hint="They appear here as soon as a conversation captures one, before any match exists."
        />
      </Card>
    )
  }

  return (
    <Card
      title={`${query.data.total} open`}
      subtitle="Captured from conversations. A match needs a need on one side and an offer on the other."
    >
      <ul className="divide-y divide-line">
        {query.data.items.map((opportunity) => (
          <li key={opportunity.id} className="px-4 py-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-ink">{opportunity.title}</p>
                <Link
                  to={`/contacts/${opportunity.contact_id}`}
                  className="text-xs text-ink-muted hover:text-ink"
                >
                  {opportunity.contact_name || 'Unknown contact'}
                </Link>
              </div>
              <Badge tone={opportunity.kind === 'need' ? 'info' : 'neutral'}>
                {opportunity.kind === 'need' ? 'Needs' : 'Offers'}
              </Badge>
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <AssertionBadge assertion={opportunity.assertion} />
              <Badge tone={opportunity.urgency === 'urgent' ? 'warning' : 'neutral'}>
                {sentence(opportunity.urgency)}
              </Badge>
              <span className="text-xs tabular text-ink-faint">
                {Math.round(opportunity.confidence * 100)}% confident ·{' '}
                {relativeTime(opportunity.created_at)}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  )
}

function MatchCard({
  match,
  busy,
  canApprove,
  onReview,
}: {
  match: Match
  busy: boolean
  canApprove: boolean
  onReview: (id: string, action: 'approve' | 'reject') => void
}) {
  const undecided = match.status === 'new' || match.status === 'reviewing'

  return (
    <article className="card flex flex-col">
      <header className="flex items-start justify-between gap-3 border-b border-line px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="info">{matchTypeLabel(match.match_type)}</Badge>
          <StatusBadge status={match.status} />
          {match.caps_applied.length > 0 && (
            <Badge tone="warning" icon="⚠">
              Capped
            </Badge>
          )}
        </div>
        <span className="shrink-0 text-xs text-ink-faint">{relativeTime(match.created_at)}</span>
      </header>

      <div className="flex items-stretch gap-3 px-4 py-3">
        <Party party={match.person_a} />
        <div className="flex shrink-0 items-center px-1 text-ink-faint" aria-hidden="true">
          ⇄
        </div>
        <Party party={match.person_b} />
      </div>

      <div className="px-4">
        <ScoreMeter score={match.score} />
        <p className="mt-1 text-xs text-ink-faint">
          Engine <span className="tabular">{match.deterministic_score}</span> · AI{' '}
          <span className="tabular">{match.llm_score ?? '—'}</span> ·{' '}
          <span className="tabular">{Math.round(match.confidence * 100)}%</span> confident
        </p>
      </div>

      {match.opportunity_text && (
        <p className="px-4 py-3 text-sm text-ink">{match.opportunity_text}</p>
      )}

      {match.reasons.length > 0 && (
        <ul className="space-y-1.5 px-4 pb-3">
          {match.reasons.slice(0, 3).map((reason, index) => (
            <li key={index} className="flex items-start gap-2 text-xs text-ink-muted">
              <span
                aria-hidden="true"
                className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full"
                style={{
                  background:
                    reason.assertion === 'explicit' ? 'var(--series-1)' : 'var(--axis)',
                }}
              />
              <span>
                {reason.text}
                {reason.assertion === 'inferred' && (
                  <span className="ml-1 text-ink-faint">(inferred)</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      <footer className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t border-line px-4 py-3">
        <Link
          to={`/opportunities/${match.id}`}
          className="text-xs font-medium text-ink-muted hover:text-ink"
        >
          Review evidence →
        </Link>
        {canApprove && undecided && (
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-secondary px-2.5 py-1 text-xs"
              disabled={busy}
              onClick={() => onReview(match.id, 'reject')}
            >
              Reject
            </button>
            <button
              type="button"
              className="btn-primary px-2.5 py-1 text-xs"
              disabled={busy}
              onClick={() => onReview(match.id, 'approve')}
            >
              {busy ? '…' : 'Approve'}
            </button>
          </div>
        )}
      </footer>
    </article>
  )
}

function Party({ party }: { party: MatchParty }) {
  return (
    <Link
      to={`/contacts/${party.contact_id}`}
      className="min-w-0 flex-1 rounded-md px-2 py-1.5 transition-colors hover:bg-raised"
    >
      <span className="flex items-center gap-2">
        <Avatar name={party.name} size={28} />
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium text-ink">{party.name}</span>
          <span className="block truncate text-xs text-ink-muted">
            {party.headline || party.industry || '—'}
          </span>
        </span>
      </span>
      {party.location && (
        <span className="mt-1 block truncate text-xs text-ink-faint">{party.location}</span>
      )}
    </Link>
  )
}
